"use client";

import { useState } from "react";
import { Check } from "lucide-react";
import { SUPPORT_EMAIL } from "@/lib/links";

type SignupFormProps = {
  /** Where the signup came from, e.g. "free-sample". Shown in the message. */
  source: string;
  submitLabel: string;
  /** Subject line of the placeholder email. */
  subject: string;
  /** Shown after submit. */
  successMessage: string;
};

/**
 * Email signup form.
 *
 * PLACEHOLDER: until an email tool (Loops, Resend, Buttondown) is connected,
 * submitting opens the visitor's mail app with a pre-filled message to
 * SUPPORT_EMAIL. The mailto URL is built at submit time so the page source
 * never contains a `mailto:` action (Lighthouse flags those on HTTPS pages).
 *
 * To connect a real tool, replace the body of `handleSubmit` with a request
 * to the tool's form endpoint (keeping the `email` and `optIn` values).
 */
export function SignupForm({
  source,
  submitLabel,
  subject,
  successMessage,
}: SignupFormProps) {
  const [sent, setSent] = useState(false);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const email = String(data.get("email") ?? "").trim();
    if (!email) return;
    const optIn = data.get("optIn") === "on";
    const body = [
      `Please send the download to ${email}.`,
      `Source: ${source}`,
      `Email me about new releases: ${optIn ? "yes" : "no"}`,
    ].join("\n");
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSent(true);
  }

  if (sent) {
    return (
      <div
        role="status"
        className="flex items-start gap-3 rounded-3xl bg-mint px-5 py-4 text-mint-ink"
      >
        <Check className="mt-0.5 h-5 w-5 flex-none" strokeWidth={3} />
        <p className="font-semibold">{successMessage}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <label htmlFor={`${source}-email`} className="sr-only">
          Email address
        </label>
        <input
          id={`${source}-email`}
          type="email"
          name="email"
          required
          autoComplete="email"
          placeholder="you@email.com"
          className="h-14 min-w-0 flex-[1_1_min(220px,100%)] rounded-full border-0 bg-white px-6 text-base text-navy outline-none ring-1 ring-navy/10 placeholder:text-muted focus-visible:ring-4 focus-visible:ring-brand/30"
        />
        <button
          type="submit"
          className="h-14 w-full rounded-full bg-brand px-7 sm:w-auto font-display text-[17px] font-semibold text-white shadow-[0_12px_24px_rgba(9,80,204,0.25)] transition-colors hover:bg-[#0742a8]"
        >
          {submitLabel}
        </button>
      </div>
      <label className="flex cursor-pointer items-start gap-3 text-sm text-ink">
        <input
          type="checkbox"
          name="optIn"
          className="mt-0.5 h-4 w-4 flex-none accent-brand"
        />
        <span>
          Also email me about new releases. Optional, and you can unsubscribe
          any time.
        </span>
      </label>
    </form>
  );
}
